/**
 * Venue endpoints #6–#10 of the frozen contract (docs/05-dev-spec.md §4) — the
 * client half of task T.C4a, talking to the LIVE T.B3a module
 * (`apps/api/src/venues/{dto,service}.ts`).
 *
 * The shapes are declared here instead of imported from `@cb-manager/shared`
 * for the reason `./dashboard` already states: the generated
 * `packages/shared/src/openapi/api-types.ts` is not re-exported through the
 * package barrel, so a workspace consumer cannot reach it. `apps/api/src/venues/dto.ts`
 * documents the same 1:1 mirror on the server side — a divergence either way is
 * a contract bug.
 *
 * One gap the live api used to make real, closed by T.B6:
 *   * #6 carries the frozen `VenueDto` field set plus the three supersets the
 *     contract serialises since v1.2 (`createdAt`/`updatedAt`/`appStatusCounts`);
 *     the roster renders the mini-badges column only when a row carries the map;
 *   * the alias dictionary had NO read at all (#9/#10 wrote, #7 did not fan
 *     aliases out), so `useVenueAliases` kept a per-session cache of what the
 *     admin had written. The additive #9.5 (`GET /venues/:id/aliases`) is the
 *     real source now — the hook just swapped its `queryFn`.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { buildQuery, requestEnvelope } from './client';
import type { DashboardStatus } from './dashboard';

/** The six application statuses, keyed the way #7 serialises `appStatusCounts`. */
export type VenueStatusCountMap = Record<DashboardStatus, number>;

/**
 * Mirrors `VenueDto` (apps/api/src/venues/dto.ts) — the frozen field set plus
 * the supersets the contract serialises since v1.2 and the sheet columns +
 * «Кол-во ДОП» the api exposes since v1.4 (T.F7 / T.R9). Every sheet field is
 * nullable: the sheet cell was empty, or (for `enrolledCount`) carried prose.
 */
export interface VenueDto {
  id: number;
  /** Master name — the byte-stable match key across sheets. */
  name: string;
  /** Output of the shared address normalizer. */
  normalizedAddress: string | null;
  /** Normalized region name (region_norm dictionary). */
  region: string | null;
  /** Assigned manager (`AppUser.id`). */
  managerUserId: number | null;
  /**
   * ADDITIVE (v1.6.1, T.V6) — EVERY manager of the venue, ascending. The frozen
   * `managerUserId` answers `managerUserIds[0] ?? null` and is deprecated: a
   * venue has held several managers since migration 0011. Optional in the type
   * on purpose — an older api sends neither, and such a row degrades to the
   * scalar without a runtime hole.
   */
  managerUserIds?: number[];
  /** Free-form curator status. */
  controlStatus: string | null;
  /** ADDITIVE (T.R8) — the venue's curator ФИО, from the teachers sheet or PATCH #8. */
  curatorName: string | null;
  /** ADDITIVE (T.R8) — the curator's phone, verbatim. */
  curatorPhone: string | null;
  /** ADDITIVE (T.R8) — the curator's e-mail, lowercased. */
  curatorEmail: string | null;
  /** `Venue.createdAt` as ISO-8601 (T.B6). */
  createdAt: string;
  /** Curator freshness — the column the roster's `sort=updatedAt` orders on. */
  updatedAt: string;
  /**
   * The venue's six status buckets, zero-filled. #7/#9.5 always compute it; #8
   * answers the all-zero map (a write has no counts at hand).
   */
  appStatusCounts: VenueStatusCountMap;
  /**
   * «Кол-во ДОП» (T.R9) — the venue's `link_registry` row count. Zero-filled
   * like the status map; #8's answer carries 0 because a write has no counts.
   */
  programsCount: number;
  // ── The «Квоты»-sheet columns (T.F7 + T.R9) ───────────────────────────────
  /** «Комментарий менеджера». */
  commentImported: string | null;
  /** «Кейс (1-6)» — mixed number/text, verbatim. */
  caseInfo: string | null;
  /** «Выбор программ» — the raw multi-line cell. */
  programsSelection: string | null;
  /** «Кол-во зачисленных на 10.09» — strict integer (the sheet's prose is null). */
  enrolledCount: number | null;
  /** «Высвободились квоты» — mixed number/text, verbatim. */
  freedQuotaNote: string | null;
  /** «Статусы для онлайн квот». */
  onlineControlStatus: string | null;
  /** «Проверка выгодных кейсов» — «верно»/«не верно». */
  profitableCasesCheck: string | null;
  /** «Ссылка на отчет куратора». */
  curatorReportUrl: string | null;
  /** «Дополнительное согласование Анна/Катя». */
  extraApproval: string | null;
  // ── The venue's TWO plan numbers (ADDITIVE v1.6, T.V1) ─────────────────────
  /** «План офлайн» — `venue_plans.plan_offline`, 0 when the venue has no row yet. */
  planOffline: number;
  /** «План онлайн» — `venue_plans.plan_online`, 0 when the venue has no row yet. */
  planOnline: number;
  // ── Партнёрские филиалы и кураторы (ADDITIVE v1.8.0, wave 9, T.Y9) ─────────
  /**
   * `REGISTRY` (every pre-wave-9 venue) | `PARTNER_ONLINE` (a partner filial).
   * Optional in the type on purpose: an older api sends neither, and every such
   * row IS a registry venue — absence degrades to the badge it should show.
   */
  kind?: VenueKind;
  /** `venues.partner_id` — set on a `PARTNER_ONLINE` row, always null on a `REGISTRY` one. */
  partnerId?: number | null;
  /** `partners.name`, resolved server-side so a row can be labelled without a second read. */
  partnerName?: string | null;
  /** `partners.plan_online` — the SHARED pool a partner branch reads its work against. */
  partnerPlanOnline?: number | null;
  /** D3 — may a curator of THIS venue manage prefill rows here (online recruitment). */
  allowCuratorChildren?: boolean;
  /**
   * ADDITIVE (v1.9.15, T.Z94) — the venue's OWN department (`venues.department`,
   * migration 0026): `OP` = Отдел привлечения, `OS` = Отдел сопровождения, `null` =
   * «не задано». Optional in the type on purpose — an older api sends neither, and
   * such a row degrades to the managers' fold (see `departments.ts`). When set, this
   * field WINS over the managers' fold in every «Отдел» consumer.
   */
  department?: VenueOwnDepartment | null;
  /** Every curator of the venue (`venue_curators`), ascending — #8 PATCHes it as a REPLACE-SET. */
  curatorUserIds?: number[];
}

/** ADDITIVE (v1.8.0, A5) — the reporting kind of a venue (`venues.kind`). */
export type VenueKind = 'REGISTRY' | 'PARTNER_ONLINE';

/**
 * ADDITIVE (v1.9.15, T.Z94) — the department a venue can carry of its own: the same
 * two codes `UserDepartment` stores for a МЕНЕДЖЕР. No `MIXED` (that bucket stays
 * derived from the managers), no `NONE` («не задано» is the column's `null`).
 */
export type VenueOwnDepartment = 'OP' | 'OS';

/** Mirrors `QuotaDto`. One row per venue × course × format. */
export interface QuotaDto {
  id: number;
  venueId: number;
  courseId: number;
  format: VenueCourseFormat;
  plan: number;
}

/**
 * Mirrors `OfflineFactPointDto` (ADDITIVE v1.4, T.W5) — one point of #7's
 * `offlineFactTrend`: the venue's OFFLINE applications that an ЕПГУ snapshot of
 * that date already carried, minus the «Отказ» ones since T.Z44 — the trend IS
 * the venue card's «занято (факт)» numerator, and a refusal occupies no seat.
 * There is no ONLINE trend on purpose (T.W4): the
 * sheet's «Факт онлайн» is 1-of-191 filled and online stays plan-only until the
 * 14.09 re-cut.
 */
export interface OfflineFactPoint {
  /** The snapshot's `exportDate` (ISO-8601) — the campaign's cut day. */
  date: string;
  count: number;
}

export type VenueCourseFormat = 'OFFLINE' | 'ONLINE';

/** Mirrors `LinkDto` — one ЕПГУ link of this venue's program list. */
export interface LinkDto {
  /** ADDITIVE (v1.3, T.F2) — `LinkRegistry.id`, the key the link writes address a row by. */
  id: number;
  courseId: number;
  /** Per-program ordinal inside one program's link list — NOT a venue id. */
  placeId: number;
  /** Primary match key: the sheet reorders, shifting placeIds. */
  url: string;
  venueId: number | null;
  venueName: string | null;
  /** ADDITIVE (v1.3, T.F2) — the program format the link serves, null when unset. */
  format: VenueCourseFormat | null;
}

/** Mirrors `VenueAliasDto` — `venueId` rides on the row since #9.5 answers a list. */
export interface VenueAliasDto {
  id: number;
  kind: AliasKind;
  value: string;
  source: AliasSource;
  /** The owning venue (`Venue.id`) — the FK #9/#10 imply with their path. */
  venueId: number;
}

export type AliasKind = 'ADDRESS' | 'NAME';
export type AliasSource = 'AUTO' | 'MANUAL';

/** #7 — `VenueDto` + the venue-scoped aggregates (+ the additive `unmatchedQueue`). */
export interface VenueDetailDto extends VenueDto {
  quotas: QuotaDto[];
  links: LinkDto[];
  /** The teacher registry is reduced to a count (T1.0 ratification). */
  teachersSummary: { total: number };
  commentCount: number;
  appStatusCounts: VenueStatusCountMap;
  /** Additive (T.B3a): applications with `venueId=null` still UNMATCHED — the repair backlog. */
  unmatchedQueue: { total: number };
  /**
   * ADDITIVE (T.W5) — «Факт офлайн по датам», ascending by cut date. Optional
   * in the type (the contract keeps it optional) so an older api that does not
   * send it yet degrades to «Срезов ЕПГУ пока нет» instead of a runtime hole.
   */
  offlineFactTrend?: OfflineFactPoint[];
}

/** The contract's `PaginationMeta` — `pageSize`, not the shared `limit`. */
export interface VenueListMeta {
  page: number;
  pageSize: number;
  total: number;
}

/** `#6` — `{ data: { venues: [...] }, meta }`. */
export interface VenueListResult {
  venues: VenueDto[];
  meta: VenueListMeta;
}

/** The filters + pagination the web is allowed to ask for (#6). */
export interface VenuesListQuery {
  region?: string;
  q?: string;
  /** ADDITIVE (wave 6, contract v1.5) — the roster's «Площадка» type-ahead. */
  venueId?: number;
  page?: number;
  pageSize?: number;
}

/** #8 body — only provided keys change; `null` clears a nullable field. */
export interface VenuePatch {
  managerUserId?: number | null;
  /**
   * ADDITIVE (v1.6.1, T.V6) — the REPLACE-SET of `venue_managers`: the array is
   * the venue's whole manager list afterwards, `[]` unassigns everyone. The
   * deprecated scalar folds into a ONE-element set on the api side, so writing
   * it from an editor that shows several managers silently DROPS the rest (Z3) —
   * new code sends this key.
   */
  managerUserIds?: number[];
  controlStatus?: string | null;
  normalizedAddress?: string | null;
  /** T.R9 — the roster columns #8 accepts. */
  profitableCasesCheck?: string | null;
  curatorReportUrl?: string | null;
  extraApproval?: string | null;
  /**
   * ADDITIVE (wave 6, contract v1.5) — the rest of the «Квоты-лист» block. They
   * stayed seed-only through v1.4.1 and the manager kept a private copy of the
   * sheet to correct them; #8 writes them now, audited like every other key.
   */
  commentImported?: string | null;
  caseInfo?: string | null;
  freedQuotaNote?: string | null;
  onlineControlStatus?: string | null;
  enrolledCount?: number | null;
  /** ADDITIVE (T.R8) — the venue's curator; `null` clears a field. */
  curatorName?: string | null;
  curatorPhone?: string | null;
  curatorEmail?: string | null;
  /**
   * ADDITIVE (v1.6, T.V1) — the venue's TWO plan numbers, «План офлайн» / «План
   * онлайн». They live on `venue_plans` (one row per venue, no programme axis);
   * non-negative integers, NO `null` — 0 is the deliberate empty plan, omitting the
   * key leaves the stored value alone.
   */
  planOffline?: number;
  planOnline?: number;
  // ── ADDITIVE (v1.8.0, wave 9, T.Y9) — the partner/kind block of #8 ─────────
  /** The reporting kind; `PARTNER_ONLINE` demands a `partnerId` (400 otherwise). */
  kind?: VenueKind;
  /** The partner whose pool this branch reads; `null` is only legal on a `REGISTRY` venue. */
  partnerId?: number | null;
  /** D3 (SR §5.3) — «разрешить куратору вносить детей (онлайн-набор)». */
  allowCuratorChildren?: boolean;
  /**
   * ADDITIVE (v1.9.15, T.Z94) — the venue's OWN department, the venue card's «Отдел»
   * select. `OP`/`OS` set it, `null` clears it back to «не задано»; omitting the key
   * leaves the stored value alone. The write is audited like every other #8 key, and
   * it wins over the managers' fold the moment it lands.
   */
  department?: VenueOwnDepartment | null;
  /** The REPLACE-SET of `venue_curators` — the only post-create editor of the curator links. */
  curatorUserIds?: number[];
}

/** #9 body. */
export interface AliasCreate {
  kind: AliasKind;
  value: string;
}

/**
 * ADDITIVE #9.6 (wave 6) — the alias edit body. Both keys optional and
 * independent; the api normalizes the value by the kind it ends up with.
 */
export interface AliasPatch {
  kind?: AliasKind;
  value?: string;
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };

// ---------------------------------------------------------------------------
// #6 — the roster
// ---------------------------------------------------------------------------

/** Default page size of the contract; the web never asks for another one. */
export const VENUES_PAGE_SIZE = 50;

/**
 * `/venues?region=…&q=…&page=…` with the DEFAULTS DROPPED: a default state must
 * not leak a parameter into the URL the api receives (page 1 and pageSize 50
 * are the contract's defaults — sending them would only noise the query log).
 */
export function venuesPath(query: VenuesListQuery = {}): string {
  const page = query.page !== undefined && query.page > 1 ? query.page : undefined;
  const pageSize =
    query.pageSize !== undefined && query.pageSize !== VENUES_PAGE_SIZE ? query.pageSize : undefined;
  // `venueId` rides the same builder — ADDITIVE (wave 6, contract v1.5), the
  // roster's «Площадка» type-ahead commits the venue's id.
  return `/venues${buildQuery({ region: query.region, q: query.q, venueId: query.venueId, page, pageSize })}`;
}

export async function fetchVenues(query: VenuesListQuery, signal?: AbortSignal): Promise<VenueListResult> {
  const envelope = await requestEnvelope<{ venues?: VenueDto[] }>('GET', venuesPath(query), ['data', 'venues'], {
    signal,
  });
  const venues = envelope.data?.venues ?? [];
  // `EnvelopeMeta` allows the shared package's `limit` spelling; the contract's
  // own meta is `{ page, pageSize, total }` and a body without one falls back
  // to the request's values, so a stub can never render a NaN page.
  const meta = envelope.meta;
  return {
    venues,
    meta: {
      page: meta?.page ?? query.page ?? 1,
      pageSize: meta?.pageSize ?? meta?.limit ?? query.pageSize ?? VENUES_PAGE_SIZE,
      total: meta?.total ?? venues.length,
    },
  };
}

/** Cache identity: every filter + page is part of the key, so a change refetches. */
export function venuesQueryKey(query: VenuesListQuery): readonly [
  string,
  { region: string | null; q: string | null; venueId: number | null; page: number; pageSize: number | null },
] {
  return [
    'venues',
    {
      region: query.region ?? null,
      q: query.q ?? null,
      // ADDITIVE (wave 6, contract v1.5) — part of the cache identity, or a
      // venue-filtered roster would be served the unfiltered one's rows.
      venueId: query.venueId ?? null,
      page: query.page ?? 1,
      // A5/T.Z18 — the «Тип» filter narrows in the browser over a 200-row answer
      // (`toListQuery` raises the page size for it), so the raised page size IS part
      // of the identity: without it a kind-filtered page 1 would be served the plain
      // page 1 the mount already fetched, and the filter would read as "nothing".
      pageSize: query.pageSize ?? null,
    },
  ];
}

/**
 * One query per (region, q, venueId, page). `placeholderData: keepPreviousData`
 * keeps the previous page's rows on screen while the next page loads, so a pager
 * click does not collapse the table into a spinner.
 */
export function useVenues(query: VenuesListQuery) {
  return useQuery({
    queryKey: venuesQueryKey(query),
    queryFn: ({ signal }) => fetchVenues(query, signal),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: 0,
  });
}

// ---------------------------------------------------------------------------
// #7 — the card
// ---------------------------------------------------------------------------

export function venueDetailPath(id: number): string {
  return `/venues/${id}`;
}

export function venueDetailQueryKey(id: number | null): readonly [string, string, number | null] {
  return ['venues', 'detail', id];
}

/** `null` (no venue selected) disables the query instead of fetching `/venues/null`. */
export function useVenue(id: number | null) {
  return useQuery({
    queryKey: venueDetailQueryKey(id),
    queryFn: async ({ signal }) =>
      (await requestEnvelope<VenueDetailDto>('GET', venueDetailPath(id as number), ['data'], { signal })).data,
    enabled: id !== null,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: 0,
  });
}

// ---------------------------------------------------------------------------
// #8 — the curator writes (ADMIN)
// ---------------------------------------------------------------------------

export async function patchVenue(id: number, patch: VenuePatch, signal?: AbortSignal): Promise<VenueDto> {
  const envelope = await requestEnvelope<VenueDto>('PATCH', venueDetailPath(id), ['data'], {
    signal,
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(patch),
  });
  return envelope.data;
}

/**
 * Writes #8 and puts the answered `VenueDto` straight into the detail cache, so
 * the header shows the saved values without waiting for a refetch (the api
 * normalizes `normalizedAddress` on the way in, so the response — not the
 * request — is the truth).
 */
export function usePatchVenue(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (patch: VenuePatch) => patchVenue(id, patch),
    onSuccess: (venue) => {
      client.setQueryData(venueDetailQueryKey(id), (old: VenueDetailDto | undefined) =>
        old ? { ...old, ...venue } : old,
      );
    },
  });
}

// ---------------------------------------------------------------------------
// #9 / #10 — the alias dictionary (ADMIN)
// ---------------------------------------------------------------------------

export function venueAliasesPath(id: number): string {
  return `/venues/${id}/aliases`;
}

export async function addAlias(id: number, body: AliasCreate): Promise<VenueAliasDto> {
  const envelope = await requestEnvelope<VenueAliasDto>('POST', venueAliasesPath(id), ['data'], {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  return envelope.data;
}

export async function removeAlias(id: number, aliasId: number): Promise<void> {
  await requestEnvelope<undefined>('DELETE', `${venueAliasesPath(id)}/${aliasId}`, ['data'], { method: 'DELETE' });
}

/** ADDITIVE #9.6 — edits one alias of this venue (stored normalized, like #9 stores it). */
export async function updateAlias(id: number, aliasId: number, patch: AliasPatch): Promise<VenueAliasDto> {
  const envelope = await requestEnvelope<VenueAliasDto>('PATCH', `${venueAliasesPath(id)}/${aliasId}`, ['data'], {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(patch),
  });
  return envelope.data;
}

/**
 * The alias TABLE of the venue card — `GET /venues/:id/aliases` (#9.5, the
 * additive read T.B6 grew into the contract; before it this hook kept a
 * per-session registry of what the admin had written).
 *
 * The TanStack cache is the store: the list survives card → roster → card
 * navigation, and the two writes below `setQueryData`/invalidate it so the table
 * moves without waiting for the refetch.
 */
export function venueAliasesQueryKey(id: number): readonly [string, string, number] {
  return ['venues', 'aliases', id];
}

const NO_ALIASES: VenueAliasDto[] = [];

export async function fetchVenueAliases(id: number, signal?: AbortSignal): Promise<VenueAliasDto[]> {
  const envelope = await requestEnvelope<{ aliases?: VenueAliasDto[] }>(
    'GET',
    venueAliasesPath(id),
    ['data', 'aliases'],
    { signal },
  );
  return envelope.data?.aliases ?? [];
}

/** The api's order (kind, then value), kept as it answers — the table does not re-sort. */
export function useVenueAliases(id: number): VenueAliasDto[] {
  return (
    useQuery({
      queryKey: venueAliasesQueryKey(id),
      queryFn: ({ signal }) => fetchVenueAliases(id, signal),
      staleTime: 15_000,
      refetchOnWindowFocus: false,
      retry: 0,
    }).data ?? NO_ALIASES
  );
}

/**
 * #9 — creates the alias, then answers it into the table (201 created, 200
 * replayed) and drops the cache so the next mount reads the server's order.
 */
export function useAddAlias(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: AliasCreate) => addAlias(id, body),
    onSuccess: (alias) => {
      client.setQueryData<VenueAliasDto[]>(venueAliasesQueryKey(id), (old) => [
        ...(old ?? NO_ALIASES).filter((row) => row.id !== alias.id),
        alias,
      ]);
      void client.invalidateQueries({ queryKey: venueAliasesQueryKey(id) });
    },
  });
}

/**
 * #9.6 (wave 6) — writes the answered row over the stale one in place (the list
 * order is the api's, and an edit never moves a row), then invalidates so the
 * next mount re-reads the server's own order.
 */
export function useUpdateAlias(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ aliasId, patch }: { aliasId: number; patch: AliasPatch }) => updateAlias(id, aliasId, patch),
    onSuccess: (alias) => {
      client.setQueryData<VenueAliasDto[]>(venueAliasesQueryKey(id), (old) =>
        (old ?? NO_ALIASES).map((row) => (row.id === alias.id ? alias : row)),
      );
      void client.invalidateQueries({ queryKey: venueAliasesQueryKey(id) });
    },
  });
}

/**
 * #10 — removes the row at once, and puts it back when the api says no
 * (a 404 for an alias that is not this venue's, a dropped session, …).
 */
export function useRemoveAlias(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (aliasId: number) => removeAlias(id, aliasId),
    onMutate: async (aliasId) => {
      await client.cancelQueries({ queryKey: venueAliasesQueryKey(id) });
      const previous = client.getQueryData<VenueAliasDto[]>(venueAliasesQueryKey(id)) ?? NO_ALIASES;
      client.setQueryData<VenueAliasDto[]>(
        venueAliasesQueryKey(id),
        previous.filter((row) => row.id !== aliasId),
      );
      return { previous };
    },
    onError: (_error, _aliasId, context) => {
      if (context) client.setQueryData(venueAliasesQueryKey(id), context.previous);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: venueAliasesQueryKey(id) });
    },
  });
}

// ---------------------------------------------------------------------------
// #6 — the venue DIRECTORY (T.F6): one page with the whole roster, for the
// type-ahead comboboxes a filter or a form needs.
// ---------------------------------------------------------------------------

/** The fields a combobox prints, plus the id it filters / writes by. */
export interface VenueDirectoryEntry {
  id: number;
  name: string;
  /** The normalized address the type-ahead searches alongside the name. */
  address: string | null;
  /**
   * ADDITIVE (T.Z16) — the normalized region a combobox option may print as its
   * secondary line. #6 has always carried it; the directory entry just did not
   * map it until the application drawer needed «name + region».
   */
  region: string | null;
  // ADDITIVE (v1.8.0, T.Y9) — the kind/partner facts the combobox consumers read.
  kind: VenueKind;
  partnerId: number | null;
  partnerName: string | null;
  /** The SHARED pool of the venue's partner — the number the settings block seeds from. */
  partnerPlanOnline: number | null;
  /**
   * ADDITIVE (wave 10, T.Z13) — the venue → user link sets the #6 payload has
   * carried since v1.6.1 / v1.8.0, now mapped through instead of dropped: the
   * users-tab venue filter matches a USER by the links, and the roster (#25)
   * ships COUNTS only, so this directory read is the one place the inverse
   * index can come from. Absent (an older api) = the filter cannot match — the
   * page says so instead of pretending the venue has no people.
   */
  managerUserIds?: number[];
  curatorUserIds?: number[];
}

/** The api's own ceiling for #6 (`apps/api/src/venues/dto.ts`) — one page of the walk. */
export const VENUE_DIRECTORY_PAGE_SIZE = 200;

export function venueDirectoryPath(page = 1): string {
  return `/venues?page=${page}&pageSize=${VENUE_DIRECTORY_PAGE_SIZE}`;
}

export const VENUE_DIRECTORY_QUERY_KEY = ['venues', 'directory'] as const;

/**
 * The whole directory, names and addresses, ordered by name (RU collation) —
 * the order a user scanning the dropdown expects. Deliberately NOT `useVenues()`: that
 * hook's cache key folds `{region, q, page}` and drops `pageSize`, so a 200-row
 * directory fetch would share a cache entry with a 50-row roster page and swap the
 * roster's rows out from under it. A private key keeps the two reads apart.
 *
 * T.Z136 — the read is a PAGE WALK, not one page. The campaign outgrew a single
 * #6 page (230 venues on 2026-09-17 against the contract's `MAX_PAGE_SIZE = 200`),
 * and #6 answers `name asc`, so the dropped tail is the alphabet's end: row 227
 * «Федеральное … имени Ярослава Мудрого» never arrived while row 83 «Кампус …
 * Мудрого» did, and typing «Мудр» into a «Площадка» search showed one venue where
 * two exist. The api carries `meta.total` for the same filtered set, so the walk
 * stops the moment the rows are all in. An empty page ends it too — a server that
 * over-reports `total` cannot loop the client — and rows dedupe by id, because an
 * insert between two pages of one walk shifts the offset window and would
 * otherwise double a venue.
 */
export async function fetchVenueDirectory(signal?: AbortSignal): Promise<VenueDirectoryEntry[]> {
  const byId = new Map<number, VenueDirectoryEntry>();
  let total = Number.POSITIVE_INFINITY;
  for (let page = 1; byId.size < total; page += 1) {
    const envelope = await requestEnvelope<{ venues?: VenueDto[] }>('GET', venueDirectoryPath(page), ['data', 'venues'], {
      signal,
    });
    const venues = envelope.data?.venues ?? [];
    for (const venue of venues) {
      byId.set(venue.id, {
        id: venue.id,
        name: venue.name,
        address: venue.normalizedAddress,
        region: venue.region,
        // A payload an older api sent reads as a registry venue with no partner —
        // which is exactly what every pre-wave-9 row was.
        kind: venue.kind ?? 'REGISTRY',
        partnerId: venue.partnerId ?? null,
        partnerName: venue.partnerName ?? null,
        partnerPlanOnline: venue.partnerPlanOnline ?? null,
        // T.Z13 — the link sets ride through only when the api sent them: `undefined`
        // (absent) and `[]` (present, empty) stay distinguishable on purpose.
        ...(venue.managerUserIds !== undefined ? { managerUserIds: venue.managerUserIds } : {}),
        ...(venue.curatorUserIds !== undefined ? { curatorUserIds: venue.curatorUserIds } : {}),
      });
    }
    // No meta (an api older than the paging meta) collapses `total` to what this
    // page brought — the walk ends after one round, exactly the pre-T.Z136 shape.
    total = envelope.meta?.total ?? byId.size;
    if (venues.length === 0) break;
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, 'ru'));
}

export function useVenueDirectory(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: VENUE_DIRECTORY_QUERY_KEY,
    queryFn: ({ signal }) => fetchVenueDirectory(signal),
    // T.Z34 — the «Адреса» pre-check turns this read optional: a caller may ask for
    // it only while its form is open (the card does), which keeps one more #6
    // request off every render that does not need it.
    enabled: options.enabled ?? true,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 0,
  });
}
